import React, { createContext } from 'react';
import noop from 'lodash/noop';

import api from '../api';
import { Voucher, VoucherInfo } from '../model';
import { ModelOptions } from '../../../types';
import { Currency } from '../../currencies/types';
import { ExchangeRate } from '../../exchangeRates/types';
import { BaseServiceVoucherInfoType } from '../../../components/Pdfs/Vouchers/voucherHelpers';
import { TravelFileTypes } from '../../travelFiles/constants/travelFileTypesOptions';
import { GetTravelFileTotalAccountingRes } from '../helpers';

export type GetVoucher<T> = (
    params?: Partial<T & { queryPresets: string; queryPresetsProps: { [key: string]: any } }>,
    options?: ModelOptions,
    updateState?: boolean
) => Promise<Array<T>>;

interface Context<T> {
    get: GetVoucher<T>;
    getOne: (params: { _id: Voucher['_id'] }, body?: { populateQuery: Array<any> }) => Promise<T>;
    getById: (id: string) => Promise<T>;
    post: (body: Partial<T>, updateState?: boolean, last?: boolean) => Promise<T>;
    put: (body: Partial<T>) => Promise<T>;
    remove?: (id: string) => Promise<string>;
    softDelete: (body: { _id: string }) => Promise<any>;
    data: T[];
    setData: (array: Array<T>) => void;
    selected: T | null;
    setSelected: (object: T) => void;
    error?: T | null | {};
}

export type AddDocumentBody = {
    clientId?: string;
    info: VoucherInfo;
    type: Voucher['type'];
    travelFileId?: Voucher['travelFileId'];
    travelFileType?: Voucher['travelFileType'];
    sourceId?: Voucher['sourceId'];
    sourceType?: Voucher['sourceType'];
    sourceNumber?: Voucher['sourceNumber'];
    targetId?: Voucher['targetId'];
    targetType?: Voucher['targetType'];
    targetNumber?: Voucher['targetNumber'];
    travelFileServiceId?: Voucher['travelFileServiceId'];
    isPaid: boolean;
    leftToPay?: number;
    currency?: Currency | string | null;
    isApproved?: boolean | null;
    isExportedToGeneralLedger?: boolean;
    isHistory?: boolean;
    notes?: Array<any>;
    createdBy?: string | null;
    /**
     * Server-only: the actor name for the Travel File Log line when there is no
     * signed-in user (`createdBy` empty) — e.g. 'automatic' for the receipt a
     * tour registration's Pelecard payment issues. Ignored when `createdBy`
     * names a user, whose own name the Log grid resolves.
     */
    createdByName?: string | null;
    /**
     * The date the document is issued. Omitted, the server stamps today — which
     * is what every caller does today. Present so a document can be back-dated
     * into the accounting period it belongs to without the caller having to
     * reach past the API.
     */
    issueDate?: Date | string | null;
};
export type BalanceGroupTravelFileBody = {
    relatedTravelFilesIds: string[];
    targetId: string;
    targetRefNumber: string;
    targetRelatedServiceId: string;
    targetCurrency: Currency;
    todayExchangeRates: ExchangeRate[];
    extraInfo: {
        companyInfo: BaseServiceVoucherInfoType['companyInfo'];
        dueDate: BaseServiceVoucherInfoType['dueDate'];
        userName: BaseServiceVoucherInfoType['userName'];
        voucherCertification: BaseServiceVoucherInfoType['voucherCertification'];
    };
};
export type GetTravelFileTotalAccountingBody = Array<{
    travelFileId: string;
    travelFileType: TravelFileTypes;
    travelFileCurrencyId: string;
    relatedServiceId: string;
}>;

export type EditVoucherBody = {
    _id: Voucher['_id'] | undefined;
    info: VoucherInfo;
    travelFileId?: Voucher['travelFileId'];
    travelFileType?: Voucher['travelFileType'];
    travelFileServiceId?: Voucher['travelFileServiceId'];
    isPaid: boolean;
    currency?: Currency | string | null | undefined;
    sourceId?: string;
    targetId?: string;
};
export type CancelVoucherBody = {
    _id: Voucher['_id'] | undefined;
    /** Also delete the card transaction in Pelecard (a same-day card receipt only). */
    deletePelecardTransaction?: boolean;
};

export type CancelVoucher = (body: CancelVoucherBody) => Promise<Voucher>;

export type ReconcileVouchersBody = {
    coveringDocument: Voucher;
    coveredDocuments: Array<Voucher>;
};

export type UnreconcileVoucherBody = {
    coveringId: Voucher['_id'];
};

/**
 * A reconciliation applies the covering document's amount across the selected
 * documents in order. When it cannot reach all of them, the ones it never
 * funded come back in `uncovered` so the caller can say so, instead of
 * reporting a success that only partly happened.
 */
export type ReconcileResult = {
    documents: Voucher[];
    appliedAmount?: number;
    unappliedAmount?: number;
    uncovered?: Array<{
        _id: Voucher['_id'];
        type: string;
        number: number | null;
        reason: string;
    }>;
};

export interface ExtendedContext extends Context<Voucher> {
    addDocument: (body: AddDocumentBody) => Promise<Voucher>;
    balanceGroupTravelFile: (body: BalanceGroupTravelFileBody) => Promise<Voucher[]>;
    getTravelFilesTotalAccounting: (
        body: GetTravelFileTotalAccountingBody
    ) => Promise<GetTravelFileTotalAccountingRes>;
    amendVoucher: (body: EditVoucherBody) => Promise<Voucher>;
    cancelVoucher: CancelVoucher;
    contextGetVouchersCount: number;
    setContextGetVouchersCount: React.Dispatch<React.SetStateAction<number>>;
    query: (query: any) => Promise<Array<Voucher>>;
    getTurnoverDocuments: (query: any) => Promise<Array<Voucher>>;
    getTurnoverSummaryDocuments: (query: any) => Promise<Array<Voucher>>;
    queryData: Voucher[];
    setQueryData: (array: Array<Voucher>) => void;
    reconcileDocuments: (body: ReconcileVouchersBody) => Promise<ReconcileResult>;
    unreconcileDocument: (body: UnreconcileVoucherBody) => Promise<Array<Voucher>>;
    resetContext: () => void;
    markVouchersAsExported: (ids: Array<string | number>) => Promise<{ updatedIds: Array<number> }>;
    enrichGlAccounts: (voucherIds: Array<string | number>) => Promise<Record<string, {
        sourceGl: string | null;
        sourceName: string | null;
        targetGl: string | null;
        targetName: string | null;
    }>>;
    getDocumentPostings: (voucherIds: Array<string | number>) => Promise<Record<string, Array<{
        lineIndex: number;
        amount: number | null;
        amountLocal: number | null;
        valueDate: string | null;
        description: string | null;
        vat: number | null;
    }>>>;
    // Reversals owed for exported documents since cancelled or amended.
    getLedgerReversals: (body: {
        toCreatedAt: string;
        fromCreatedAt?: string;
        includeExported?: boolean;
        documentIds?: Array<string | number>;
    }) => Promise<
        Array<{
            _id: number;
            documentId: number;
            replacedByDocumentId: number | null;
            reason: 'cancel' | 'amend';
            cancelledAt: string;
            exportedAt: string | null;
        }>
    >;
    markLedgerReversalsExported: (ids: Array<string | number>) => Promise<{ updatedIds: Array<number> }>;
    // Documents the export holds back: their travel file has not opened yet.
    getLedgerHeldBack: (body: { toCreatedAt: string; fromCreatedAt?: string; stamp?: boolean }) => Promise<
        Array<{
            _id: number;
            number: number | string | null;
            type: string;
            status: string | null;
            travelFileNumber: string | null;
            reason: string;
        }>
    >;
}

const context = createContext<ExtendedContext>({
    get: api.get,
    getOne: api.getOne,
    getById: api.getById,
    post: api.post,
    put: api.put,
    remove: api.remove,
    softDelete: api.softDelete,
    data: [],
    setData: noop,
    selected: null,
    setSelected: noop,
    error: null,
    addDocument: api.addDocument,
    balanceGroupTravelFile: api.balanceGroupTravelFile,
    getTravelFilesTotalAccounting: api.getTravelFilesTotalAccounting,
    amendVoucher: api.amendVoucher,
    cancelVoucher: api.cancelVoucher,
    contextGetVouchersCount: 0,
    setContextGetVouchersCount: noop,
    query: api.query,
    getTurnoverDocuments: api.getTurnoverDocuments,
    getTurnoverSummaryDocuments: api.getTurnoverSummaryDocuments,
    queryData: [],
    setQueryData: noop,
    reconcileDocuments: api.reconcileDocuments,
    unreconcileDocument: api.unreconcileDocument,
    resetContext: noop,
    markVouchersAsExported: api.markVouchersAsExported,
    enrichGlAccounts: api.enrichGlAccounts,
    getDocumentPostings: api.getDocumentPostings,
    getLedgerReversals: api.getLedgerReversals,
    markLedgerReversalsExported: api.markLedgerReversalsExported,
    getLedgerHeldBack: api.getLedgerHeldBack
});
export default context;
